import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BaseDialog } from '@/components/ui/Dialog';
import { ActionButton } from '@/components/ui/ActionButton';
import { FormField } from '@/components/forms/FormField';
import { useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api';
import {
  defaultsFrom,
  firstValue,
  type ServiceInvoiceLineState,
  type ServiceInvoiceFormState,
} from './serviceInvoiceDefaults';
import type { APPayload } from './types';
import { deriveIdempotencyKey } from './idempotency';

export interface ServiceInvoiceModalProps {
  requestId: string;
  payload: APPayload;
  // Optimistic-concurrency timestamp from the parent ApprovalRequest. Used as
  // the basis for the idempotency key and the Post-to-Oracle body.
  updatedAt: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Slice 6 GO (2026-05-09): the modal posts directly to Oracle through the
// Flow B Webhook Post Service chain. Setting VITE_AP_SERVICE_POST_ENABLED=
// false reverts to a disabled CTA + tooltip so deploys can be staged.
const POST_ENABLED =
  (import.meta.env?.VITE_AP_SERVICE_POST_ENABLED as string | undefined) !== 'false';

interface ServiceBookLov {
  service_book_id: number | string;
  service_book_name: string;
}

interface AccountCodeLov {
  code_combination_id: string;
  code_combination_value: string;
  code_combination_desc: string;
  display?: string;
}

interface TaxGroupLov {
  stax_group_id: number | string;
  stax_group_desc: string;
  primary_rate?: number | string | null;
  total_rate?: number | string | null;
}

interface ServiceItemLov {
  service_item_id: string;
  service_item_code?: string | null;
  service_item_desc?: string | null;
  primary_uom_id?: number | string | null;
}

interface PaymentTermLov {
  payment_term_id: number | string;
  payment_term_desc: string;
  short_desc?: string | null;
  no_of_days?: number | string | null;
}

function numericValue(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function roundUpWholeRupee(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.ceil(value);
}

function formatAmount(value: number): string {
  if (!Number.isFinite(value)) return '';
  return String(roundUpWholeRupee(value));
}

function appendOption(
  options: { value: string; label: string }[],
  value: string | number | null | undefined,
  label: string,
): { value: string; label: string }[] {
  const normalized = String(value ?? '').trim();
  if (!normalized || options.some((option) => option.value === normalized)) return options;
  return [{ value: normalized, label }, ...options];
}

function paymentTermFallbackLabel(value: string): string {
  return value === '2' ? '2 - CREDIT' : `${value} - Saved credit term`;
}

function serviceLinePostAmount(line: ServiceInvoiceLineState): number {
  if (line.amount !== '') return roundUpWholeRupee(numericValue(line.amount));
  return (
    roundUpWholeRupee(numericValue(line.taxable_amount)) +
    roundUpWholeRupee(numericValue(line.non_taxable_amount))
  );
}

function serviceLineTotals(lines: ServiceInvoiceLineState[]) {
  const amountTotal = lines.reduce((sum, line) => sum + serviceLinePostAmount(line), 0);
  const taxableTotal = lines.reduce(
    (sum, line) => sum + roundUpWholeRupee(numericValue(line.taxable_amount)),
    0,
  );
  const nonTaxableTotal = lines.reduce(
    (sum, line) => sum + roundUpWholeRupee(numericValue(line.non_taxable_amount)),
    0,
  );
  const taxTotal = lines.reduce(
    (sum, line) =>
      sum + (line.stax_checked ? roundUpWholeRupee(numericValue(line.tax_amount)) : 0),
    0,
  );
  return {
    amountTotal,
    taxableTotal,
    nonTaxableTotal,
    taxTotal,
    grossTotal: amountTotal + taxTotal,
  };
}

export function ServiceInvoiceModal({
  requestId,
  payload,
  updatedAt,
  open,
  onClose,
  onSuccess,
}: ServiceInvoiceModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ServiceInvoiceFormState>(() => defaultsFrom(payload));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [accountSearch, setAccountSearch] = useState('');
  const [activeLineIndex, setActiveLineIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setForm(defaultsFrom(payload));
      setErrors({});
      setActiveLineIndex(0);
    }
  }, [open, payload]);

  const set = <K extends keyof ServiceInvoiceFormState>(
    key: K,
    value: ServiceInvoiceFormState[K],
  ) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setLine = <K extends keyof ServiceInvoiceLineState>(
    index: number,
    key: K,
    value: ServiceInvoiceLineState[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      service_lines: prev.service_lines.map((line, i) =>
        i === index ? { ...line, [key]: value } : line,
      ),
    }));
  };

  const addLine = () => {
    setForm((prev) => {
      const source = prev.service_lines[prev.service_lines.length - 1] ?? {
        service_item_id: prev.service_item_id || '2010589',
        service_item_code: prev.service_item_code,
        service_item_desc: prev.service_item_desc,
        code_combination_id: prev.code_combination_id,
        code_combination_value: prev.code_combination_value,
        code_combination_desc: prev.code_combination_desc,
      };
      return {
        ...prev,
        service_lines: [
          ...prev.service_lines,
          {
            description: '',
            service_item_id: source.service_item_id || '2010589',
            service_item_code: source.service_item_code || '',
            service_item_desc: source.service_item_desc || '',
            code_combination_id: source.code_combination_id || '',
            code_combination_value: source.code_combination_value || '',
            code_combination_desc: source.code_combination_desc || '',
            amount: '',
            taxable_amount: '',
            non_taxable_amount: '',
            stax_checked: false,
            stax_group_id: '',
            stax_group_desc: '',
            stax_rate: '',
            tax_amount: '',
          },
        ],
      };
    });
  };

  const removeLine = (index: number) => {
    const nextLength = Math.max(1, form.service_lines.length - 1);
    setActiveLineIndex((current) => {
      const shifted = index <= current ? current - 1 : current;
      return Math.max(0, Math.min(shifted, nextLength - 1));
    });
    setForm((prev) => ({
      ...prev,
      service_lines:
        prev.service_lines.length <= 1
          ? prev.service_lines
          : prev.service_lines.filter((_, i) => i !== index),
    }));
  };

  const totals = useMemo(() => serviceLineTotals(form.service_lines), [form.service_lines]);
  const grossAmountDisplay = formatAmount(totals.grossTotal);

  const subtotalDisplay = useMemo(() => {
    if (form.service_lines.length > 0) {
      return formatAmount(totals.amountTotal);
    }
    const v = firstValue(payload.subtotal_before_tax, payload.subtotal);
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    return Number.isFinite(n) ? formatAmount(n) : String(v);
  }, [form.service_lines.length, payload.subtotal_before_tax, payload.subtotal, totals.amountTotal]);

  const validate = (
    state: ServiceInvoiceFormState,
  ): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!state.service_book_id || Number.isNaN(Number(state.service_book_id))) {
      errs.service_book_id = 'Required.';
    }
    if (!state.supplier_invoice_ref) errs.supplier_invoice_ref = 'Required.';
    if (!state.invoice_date) errs.invoice_date = 'Required.';
    if (!state.payment_term_id || Number.isNaN(Number(state.payment_term_id))) {
      errs.payment_term_id = 'Required.';
    }
    if (state.service_lines.length === 0) {
      errs.service_lines = 'At least one service line is required.';
    }
    state.service_lines.forEach((line, index) => {
      if (!line.service_item_id) errs[`line-${index}-service_item_id`] = 'Required.';
      if (!line.code_combination_id) errs[`line-${index}-code_combination_id`] = 'Required.';
      const amount = Number(line.amount || 0);
      const taxable = Number(line.taxable_amount || 0);
      const nonTaxable = Number(line.non_taxable_amount || 0);
      if (!amount && !taxable && !nonTaxable) {
        errs[`line-${index}-amount`] = 'Required.';
      }
      if (line.stax_checked) {
        if (!line.stax_group_id || Number.isNaN(Number(line.stax_group_id))) {
          errs[`line-${index}-stax_group_id`] = 'Required.';
        }
        if (!line.tax_amount || Number.isNaN(Number(line.tax_amount))) {
          errs[`line-${index}-tax_amount`] = 'Required.';
        }
      }
    });
    return errs;
  };

  const buildPreflight = (state: ServiceInvoiceFormState): Record<string, unknown> => {
    const totals = serviceLineTotals(state.service_lines);
    const firstLine = state.service_lines[0];
    const serviceLines = state.service_lines.map((line) => {
      const body: Record<string, unknown> = {
        description: line.description,
        service_item_id: line.service_item_id,
        service_item_code: line.service_item_code,
        service_item_desc: line.service_item_desc,
        code_combination_id: line.code_combination_id,
        code_combination_value: line.code_combination_value,
        code_combination_desc: line.code_combination_desc,
        stax_checked: line.stax_checked,
      };
      if (line.amount) body.amount = roundUpWholeRupee(Number(line.amount));
      if (line.taxable_amount) body.taxable_amount = roundUpWholeRupee(Number(line.taxable_amount));
      if (line.non_taxable_amount) {
        body.non_taxable_amount = roundUpWholeRupee(Number(line.non_taxable_amount));
      }
      if (line.stax_checked) {
        body.stax_group_id = Number(line.stax_group_id);
        body.stax_group_desc = line.stax_group_desc;
        if (line.stax_rate) body.stax_rate = Number(line.stax_rate);
        body.tax_amount = roundUpWholeRupee(Number(line.tax_amount));
      }
      return body;
    });
    const body: Record<string, unknown> = {
      service_book_id: Number(state.service_book_id),
      service_book_name: state.service_book_name,
      service_item_id: firstLine?.service_item_id ?? state.service_item_id,
      service_item_code: firstLine?.service_item_code ?? state.service_item_code,
      service_item_desc: firstLine?.service_item_desc ?? state.service_item_desc,
      code_combination_id: firstLine?.code_combination_id ?? state.code_combination_id,
      code_combination_value:
        firstLine?.code_combination_value ?? state.code_combination_value,
      code_combination_desc: firstLine?.code_combination_desc ?? state.code_combination_desc,
      supplier_invoice_ref: state.supplier_invoice_ref,
      supplier_id: Number(firstValue(payload.supplier_id, payload.supplierId) || 0) || undefined,
      payment_term_id: Number(state.payment_term_id),
      gross_amount: totals.grossTotal,
      invoice_date: state.invoice_date,
      stax_checked: serviceLines.some((line) => line.stax_checked === true),
      service_lines: serviceLines,
    };
    if (totals.taxableTotal) body.taxable_amount = totals.taxableTotal;
    if (totals.nonTaxableTotal) body.non_taxable_amount = totals.nonTaxableTotal;
    if (totals.taxTotal) body.tax_amount = totals.taxTotal;
    const firstTaxLine = state.service_lines.find((line) => line.stax_checked);
    if (firstTaxLine?.stax_group_id) body.stax_group_id = Number(firstTaxLine.stax_group_id);
    if (state.note) body.note = state.note;
    return body;
  };

  const postMutation = useMutation({
    mutationFn: async (preflight: Record<string, unknown>) => {
      const idem = deriveIdempotencyKey(requestId, updatedAt, 'post-service-final');
      const res = await apiClient.post(
        `/automations/ap-purchase-invoice/${requestId}/post-service-final`,
        { preflight, updated_at: updatedAt },
        { headers: { 'Idempotency-Key': idem } },
      );
      return res.data as {
        ok: boolean;
        duplicate?: boolean;
        oracle_result?: { ref_no?: string };
        duplicate_of?: { ref_no?: string; purchase_invoice_id?: string };
      };
    },
    onSuccess: (data) => {
      if (data.duplicate) {
        const refNo = data.duplicate_of?.ref_no ?? data.duplicate_of?.purchase_invoice_id ?? '';
        toast({
          variant: 'warning',
          title: 'Already exists in Oracle',
          description: refNo
            ? `Moved to Duplicate history for ${refNo}.`
            : 'Moved to Duplicate history.',
        });
        void queryClient.invalidateQueries({ queryKey: ['queue', 'ap-purchase-invoice'] });
        void queryClient.invalidateQueries({ queryKey: ['history', 'ap-purchase-invoice'] });
        onSuccess();
        onClose();
        return;
      }
      const refNo = data.oracle_result?.ref_no ?? '(pending)';
      toast({
        variant: 'success',
        title: `Posted as PI #${refNo}`,
        description: 'Service invoice posted to Oracle.',
      });
      void queryClient.invalidateQueries({ queryKey: ['queue', 'ap-purchase-invoice'] });
      void queryClient.invalidateQueries({ queryKey: ['history', 'ap-purchase-invoice'] });
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } }; message?: string })?.response
          ?.data?.detail ??
        (err as { message?: string })?.message ??
        'Post failed';
      toast({
        variant: 'error',
        title: 'Could not post to Oracle',
        description: detail,
      });
    },
  });

  const handlePost = () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    postMutation.mutate(buildPreflight(form));
  };

  const isPending = postMutation.isPending;

  const booksQuery = useQuery({
    queryKey: ['ap-service-lovs', 'books'],
    queryFn: async () =>
      (await apiClient.get('/automations/ap-purchase-invoice/service-lovs/books')).data as {
        items: ServiceBookLov[];
      },
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });
  const taxGroupsQuery = useQuery({
    queryKey: ['ap-service-lovs', 'tax-groups'],
    queryFn: async () =>
      (await apiClient.get('/automations/ap-purchase-invoice/service-lovs/tax-groups')).data as {
        items: TaxGroupLov[];
      },
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });
  const serviceItemsQuery = useQuery({
    queryKey: ['ap-service-lovs', 'service-items'],
    queryFn: async () =>
      (await apiClient.get('/automations/ap-purchase-invoice/service-lovs/service-items')).data as {
        items: ServiceItemLov[];
      },
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });
  const paymentTermsQuery = useQuery({
    queryKey: ['ap-service-lovs', 'payment-terms'],
    queryFn: async () =>
      (
        await apiClient.get('/automations/ap-purchase-invoice/service-lovs/payment-terms')
      ).data as {
        items: PaymentTermLov[];
      },
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });
  const accountSearchTerm = accountSearch.trim();
  const accountCodesQuery = useQuery({
    queryKey: ['ap-service-lovs', 'account-codes', accountSearchTerm],
    queryFn: async () =>
      (
        await apiClient.get('/automations/ap-purchase-invoice/service-lovs/account-codes', {
          params: { q: accountSearchTerm, limit: 20 },
        })
      ).data as { items: AccountCodeLov[] },
    enabled: open && accountSearchTerm.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const bookOptions = appendOption(
    (booksQuery.data?.items ?? []).map((book) => ({
      value: String(book.service_book_id),
      label: `${book.service_book_id} - ${book.service_book_name}`,
    })),
    form.service_book_id,
    `${form.service_book_id} - ${form.service_book_name || 'Saved service book'}`,
  );
  const taxOptions = (taxGroupsQuery.data?.items ?? []).map((tax) => ({
    value: String(tax.stax_group_id),
    label: `${tax.stax_group_id} - ${tax.stax_group_desc} (${tax.primary_rate ?? tax.total_rate ?? ''}%)`,
  }));
  const itemOptions = (serviceItemsQuery.data?.items ?? []).map((item) => ({
    value: String(item.service_item_id),
    label: `${item.service_item_id} - ${item.service_item_desc ?? item.service_item_code ?? ''}`,
  }));
  const paymentTermOptions = appendOption(
    (paymentTermsQuery.data?.items ?? []).map((term) => ({
      value: String(term.payment_term_id),
      label: `${term.payment_term_id} - ${term.payment_term_desc}${
        term.no_of_days ? ` (${term.no_of_days} days)` : ''
      }`,
    })),
    form.payment_term_id,
    paymentTermFallbackLabel(form.payment_term_id),
  );

  const itemOptionsForLine = (line: ServiceInvoiceLineState) =>
    appendOption(
      itemOptions,
      line.service_item_id,
      `${line.service_item_id} - ${
        line.service_item_desc || line.service_item_code || 'Saved service item'
      }`,
    );

  const taxOptionsForLine = (line: ServiceInvoiceLineState) =>
    appendOption(
      taxOptions,
      line.stax_group_id,
      `${line.stax_group_id} - ${line.stax_group_desc || 'Saved tax group'}`,
    );

  const applyBook = (bookId: string) => {
    const book = booksQuery.data?.items.find((item) => String(item.service_book_id) === bookId);
    setForm((prev) => ({
      ...prev,
      service_book_id: bookId,
      service_book_name: book?.service_book_name ?? prev.service_book_name,
    }));
  };

  const applyAccount = (lineIndex: number, account: AccountCodeLov) => {
    setForm((prev) => ({
      ...prev,
      code_combination_id:
        lineIndex === 0 ? account.code_combination_id : prev.code_combination_id,
      code_combination_value:
        lineIndex === 0 ? account.code_combination_value : prev.code_combination_value,
      code_combination_desc:
        lineIndex === 0 ? account.code_combination_desc : prev.code_combination_desc,
      service_lines: prev.service_lines.map((line, i) =>
        i === lineIndex
          ? {
              ...line,
              code_combination_id: account.code_combination_id,
              code_combination_value: account.code_combination_value,
              code_combination_desc: account.code_combination_desc,
            }
          : line,
      ),
    }));
  };

  const applyItem = (lineIndex: number, itemId: string) => {
    const item = serviceItemsQuery.data?.items.find((row) => String(row.service_item_id) === itemId);
    setForm((prev) => ({
      ...prev,
      service_item_id: lineIndex === 0 ? itemId : prev.service_item_id,
      service_item_code:
        lineIndex === 0 ? String(item?.service_item_code ?? '') : prev.service_item_code,
      service_item_desc:
        lineIndex === 0 ? String(item?.service_item_desc ?? '') : prev.service_item_desc,
      service_lines: prev.service_lines.map((line, i) =>
        i === lineIndex
          ? {
              ...line,
              service_item_id: itemId,
              service_item_code: String(item?.service_item_code ?? ''),
              service_item_desc: String(item?.service_item_desc ?? ''),
            }
          : line,
      ),
    }));
  };

  const applyTaxGroup = (lineIndex: number, taxId: string) => {
    const tax = taxGroupsQuery.data?.items.find((row) => String(row.stax_group_id) === taxId);
    setLine(lineIndex, 'stax_group_id', taxId);
    setLine(lineIndex, 'stax_group_desc', tax?.stax_group_desc ?? '');
    setLine(lineIndex, 'stax_rate', String(tax?.primary_rate ?? tax?.total_rate ?? ''));
  };

  const setLineSalesTax = (lineIndex: number, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      service_lines: prev.service_lines.map((line, i) => {
        if (i !== lineIndex) return line;
        if (!checked && !line.stax_checked) return line;
        if (checked) {
          const restoredTax = numericValue(line.suspended_tax_amount);
          if (!restoredTax) return { ...line, stax_checked: true };
          return {
            ...line,
            amount: formatAmount(Math.max(0, serviceLinePostAmount(line) - restoredTax)),
            taxable_amount: formatAmount(Math.max(0, numericValue(line.taxable_amount) - restoredTax)),
            stax_checked: true,
            stax_group_id: line.suspended_stax_group_id ?? line.stax_group_id,
            stax_group_desc: line.suspended_stax_group_desc ?? line.stax_group_desc,
            stax_rate: line.suspended_stax_rate ?? line.stax_rate,
            tax_amount: formatAmount(restoredTax),
            suspended_stax_group_id: undefined,
            suspended_stax_group_desc: undefined,
            suspended_stax_rate: undefined,
            suspended_tax_amount: undefined,
          };
        }
        const tax = numericValue(line.tax_amount);
        return {
          ...line,
          amount: formatAmount(serviceLinePostAmount(line) + tax),
          taxable_amount: formatAmount(numericValue(line.taxable_amount) + tax),
          non_taxable_amount: '',
          stax_checked: false,
          suspended_stax_group_id: line.stax_group_id,
          suspended_stax_group_desc: line.stax_group_desc,
          suspended_stax_rate: line.stax_rate,
          suspended_tax_amount: line.tax_amount,
          stax_group_id: '',
          stax_group_desc: '',
          stax_rate: '',
          tax_amount: '',
        };
      }),
    }));
  };

  return (
    <BaseDialog
      open={open}
      onClose={() => {
        if (isPending) return;
        onClose();
      }}
      title="Prepare service invoice"
      description="Review and edit the prefilled service-invoice values. Closing this dialog leaves the row untouched in the GRN_NOT_FOUND bucket; only Validate and post writes anything."
      size="xl"
      footer={
        <>
          <ActionButton
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </ActionButton>
          <span title={POST_ENABLED ? '' : 'Service Oracle write disabled in this environment'}>
            <ActionButton
              type="button"
              onClick={handlePost}
              disabled={isPending || !POST_ENABLED}
              loading={postMutation.isPending}
            >
              Validate and post
            </ActionButton>
          </span>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-12 tablet:grid-cols-2">
        <FormField
          kind="select"
          label="Book"
          required
          value={form.service_book_id}
          onChange={applyBook}
          error={errors.service_book_id}
          placeholder={booksQuery.isLoading ? 'Loading books...' : 'Select book'}
          options={
            bookOptions.length
              ? bookOptions
              : form.service_book_id
                ? [
                    {
                      value: form.service_book_id,
                      label: `${form.service_book_id} - ${form.service_book_name}`,
                    },
                  ]
                : []
          }
        />
        <FormField
          kind="text"
          label="Service book name"
          value={form.service_book_name}
          disabled
        />
        <FormField
          kind="text"
          label="Supplier invoice ref"
          required
          value={form.supplier_invoice_ref}
          onChange={(v) => set('supplier_invoice_ref', v)}
          error={errors.supplier_invoice_ref}
        />
        <FormField
          kind="date"
          label="Invoice date"
          required
          value={form.invoice_date}
          onChange={(v) => set('invoice_date', v)}
          error={errors.invoice_date}
        />
        <FormField
          kind="select"
          label="Credit term"
          required
          value={form.payment_term_id}
          onChange={(v) => set('payment_term_id', v)}
          error={errors.payment_term_id}
          placeholder={paymentTermsQuery.isLoading ? 'Loading credit terms...' : 'Select credit term'}
          options={
            paymentTermOptions.length
              ? paymentTermOptions
              : form.payment_term_id
                ? [{ value: form.payment_term_id, label: form.payment_term_id }]
                : []
          }
        />
        <FormField
          kind="text"
          label="Subtotal before tax"
          value={subtotalDisplay}
          disabled
        />
        <FormField
          kind="text"
          label="Vendor"
          value={String(firstValue(payload.vendor, payload.supplier) ?? '—')}
          disabled
        />
        <FormField
          kind="text"
          label="NTN"
          value={String(firstValue(payload.ntn, payload.supplier_ntn) ?? '—')}
          disabled
        />
        <FormField
          kind="text"
          label="PO reference"
          value={String(firstValue(payload.po_no, payload.po_number) ?? '—')}
          disabled
        />
        <div className="tablet:col-span-2 rounded-md border border-neutral-200">
          <div className="flex items-center justify-between border-b border-neutral-200 px-12 py-8">
            <h3 className="text-sm font-semibold text-neutral-800">Service lines</h3>
            <ActionButton type="button" variant="secondary" onClick={addLine} disabled={isPending}>
              Add line
            </ActionButton>
          </div>
          {errors.service_lines && (
            <p className="px-12 pt-8 text-xs text-error-500" role="alert">
              {errors.service_lines}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse text-left text-sm">
              <thead className="bg-neutral-50 text-xs font-semibold uppercase text-neutral-500">
                <tr>
                  <th className="px-8 py-6">Description</th>
                  <th className="px-8 py-6">Service item</th>
                  <th className="px-8 py-6">Account code</th>
                  <th className="px-8 py-6">Amount</th>
                  <th className="px-8 py-6">Taxable</th>
                  <th className="px-8 py-6">Non-taxable</th>
                  <th className="px-8 py-6">Tax group</th>
                  <th className="px-8 py-6">Tax</th>
                  <th className="px-8 py-6" aria-label="Line actions" />
                </tr>
              </thead>
              <tbody>
                {form.service_lines.map((line, index) => (
                  <tr
                    key={index}
                    className={index === activeLineIndex ? 'bg-primary-500/5' : undefined}
                    onFocus={() => setActiveLineIndex(index)}
                  >
                    <td className="w-[220px] px-8 py-8 align-top">
                      <textarea
                        aria-label={`Service line ${index + 1} description`}
                        value={line.description}
                        onChange={(e) => setLine(index, 'description', e.target.value)}
                        className="h-20 w-full rounded-md border border-neutral-200 px-8 py-6 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </td>
                    <td className="w-[190px] px-8 py-8 align-top">
                      <select
                        aria-label={`Service line ${index + 1} item`}
                        value={line.service_item_id}
                        onChange={(e) => applyItem(index, e.target.value)}
                        className="h-9 w-full rounded-md border border-neutral-200 bg-neutral-0 px-8 text-sm"
                      >
                        {itemOptionsForLine(line).map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                      {errors[`line-${index}-service_item_id`] && (
                        <p className="mt-4 text-xs text-error-500">
                          {errors[`line-${index}-service_item_id`]}
                        </p>
                      )}
                    </td>
                    <td className="w-[230px] px-8 py-8 align-top">
                      <button
                        type="button"
                        onClick={() => setActiveLineIndex(index)}
                        className="w-full rounded-md border border-neutral-200 bg-neutral-0 px-8 py-6 text-left text-sm hover:border-primary-500"
                      >
                        <span className="block font-medium text-neutral-800">
                          {line.code_combination_value || 'Select account'}
                        </span>
                        <span className="block truncate text-xs text-neutral-500">
                          {line.code_combination_desc ||
                            line.code_combination_id ||
                            'No account selected'}
                        </span>
                      </button>
                      {errors[`line-${index}-code_combination_id`] && (
                        <p className="mt-4 text-xs text-error-500">
                          {errors[`line-${index}-code_combination_id`]}
                        </p>
                      )}
                    </td>
                    <td className="w-[120px] px-8 py-8 align-top">
                      <input
                        aria-label={`Service line ${index + 1} amount`}
                        type="number"
                        step="0.01"
                        value={line.amount}
                        onChange={(e) => setLine(index, 'amount', e.target.value)}
                        className="h-9 w-full rounded-md border border-neutral-200 px-8 text-sm"
                      />
                      {errors[`line-${index}-amount`] && (
                        <p className="mt-4 text-xs text-error-500">{errors[`line-${index}-amount`]}</p>
                      )}
                    </td>
                    <td className="w-[120px] px-8 py-8 align-top">
                      <input
                        aria-label={`Service line ${index + 1} taxable amount`}
                        type="number"
                        step="0.01"
                        value={line.taxable_amount}
                        onChange={(e) => setLine(index, 'taxable_amount', e.target.value)}
                        className="h-9 w-full rounded-md border border-neutral-200 px-8 text-sm"
                      />
                    </td>
                    <td className="w-[120px] px-8 py-8 align-top">
                      <input
                        aria-label={`Service line ${index + 1} non-taxable amount`}
                        type="number"
                        step="0.01"
                        value={line.non_taxable_amount}
                        onChange={(e) => setLine(index, 'non_taxable_amount', e.target.value)}
                        className="h-9 w-full rounded-md border border-neutral-200 px-8 text-sm"
                      />
                    </td>
                    <td className="w-[170px] px-8 py-8 align-top">
                      <label className="mb-4 flex items-center gap-6 text-xs text-neutral-600">
                        <input
                          type="checkbox"
                          checked={line.stax_checked}
                          onChange={(e) => setLineSalesTax(index, e.target.checked)}
                        />
                        S-Tax
                      </label>
                      <select
                        aria-label={`Service line ${index + 1} tax group`}
                        value={line.stax_group_id}
                        disabled={!line.stax_checked}
                        onChange={(e) => applyTaxGroup(index, e.target.value)}
                        className="h-9 w-full rounded-md border border-neutral-200 bg-neutral-0 px-8 text-sm disabled:bg-neutral-50"
                      >
                        <option value="">Select</option>
                        {taxOptionsForLine(line).map((tax) => (
                          <option key={tax.value} value={tax.value}>
                            {tax.label}
                          </option>
                        ))}
                      </select>
                      {errors[`line-${index}-stax_group_id`] && (
                        <p className="mt-4 text-xs text-error-500">
                          {errors[`line-${index}-stax_group_id`]}
                        </p>
                      )}
                    </td>
                    <td className="w-[120px] px-8 py-8 align-top">
                      <input
                        aria-label={`Service line ${index + 1} tax amount`}
                        type="number"
                        step="0.01"
                        disabled={!line.stax_checked}
                        value={line.tax_amount}
                        onChange={(e) => setLine(index, 'tax_amount', e.target.value)}
                        className="h-9 w-full rounded-md border border-neutral-200 px-8 text-sm disabled:bg-neutral-50"
                      />
                      {errors[`line-${index}-tax_amount`] && (
                        <p className="mt-4 text-xs text-error-500">
                          {errors[`line-${index}-tax_amount`]}
                        </p>
                      )}
                    </td>
                    <td className="px-8 py-8 align-top">
                      <ActionButton
                        type="button"
                        variant="secondary"
                        onClick={() => removeLine(index)}
                        disabled={isPending || form.service_lines.length <= 1}
                      >
                        Remove
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-neutral-200 bg-neutral-50 text-sm font-semibold text-neutral-800">
                <tr>
                  <td className="px-8 py-8" colSpan={3}>
                    Totals
                  </td>
                  <td className="px-8 py-8">{formatAmount(totals.amountTotal)}</td>
                  <td className="px-8 py-8">{formatAmount(totals.taxableTotal)}</td>
                  <td className="px-8 py-8">{formatAmount(totals.nonTaxableTotal)}</td>
                  <td className="px-8 py-8">Tax</td>
                  <td className="px-8 py-8">{formatAmount(totals.taxTotal)}</td>
                  <td className="px-8 py-8" />
                </tr>
                <tr className="border-t border-neutral-200">
                  <td className="px-8 py-8 text-right" colSpan={7}>
                    Gross amount
                  </td>
                  <td className="px-8 py-8" colSpan={2}>
                    <output aria-label="Gross amount">{grossAmountDisplay}</output>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <div className="tablet:col-span-2 rounded-md border border-neutral-200 p-12">
          <div className="grid grid-cols-1 gap-12">
            <FormField
              kind="text"
              label={`Account code search for line ${activeLineIndex + 1}`}
              value={accountSearch}
              onChange={setAccountSearch}
              placeholder="Search code, ID, or description"
            />
            <div className="max-h-72 overflow-auto rounded-md border border-neutral-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-neutral-50 text-xs font-semibold uppercase text-neutral-500">
                  <tr>
                    <th className="px-8 py-6">Code</th>
                    <th className="px-8 py-6">Description</th>
                    <th className="px-8 py-6" aria-label="Select account" />
                  </tr>
                </thead>
                <tbody>
                  {accountSearchTerm.length === 0 && (
                    <tr>
                      <td className="px-8 py-8 text-neutral-500" colSpan={3}>
                        Search to load account codes.
                      </td>
                    </tr>
                  )}
                  {accountCodesQuery.isError && (
                    <tr>
                      <td className="px-8 py-8 text-error-500" colSpan={3}>
                        Account code LOV could not load. Search again.
                      </td>
                    </tr>
                  )}
                  {accountCodesQuery.isFetching && (
                    <tr>
                      <td className="px-8 py-8 text-neutral-500" colSpan={3}>
                        Loading account codes...
                      </td>
                    </tr>
                  )}
                  {!accountCodesQuery.isError && (accountCodesQuery.data?.items ?? []).map((account) => (
                    <tr key={account.code_combination_id} className="border-t border-neutral-100">
                      <td className="px-8 py-6 font-medium text-neutral-800">
                        {account.code_combination_value}
                      </td>
                      <td className="px-8 py-6 text-neutral-600">{account.code_combination_desc}</td>
                      <td className="px-8 py-6 text-right">
                        <ActionButton
                          type="button"
                          variant="secondary"
                          onClick={() => applyAccount(activeLineIndex, account)}
                        >
                          Use
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                  {accountSearchTerm.length > 0 &&
                    !accountCodesQuery.isError &&
                    !accountCodesQuery.isFetching &&
                    accountCodesQuery.data?.items?.length === 0 && (
                    <tr>
                      <td className="px-8 py-8 text-neutral-500" colSpan={3}>
                        No account codes found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="tablet:col-span-2">
          <FormField
            kind="textarea"
            label="Note (audit trail)"
            value={form.note}
            onChange={(v) => set('note', v)}
            rows={2}
          />
        </div>
      </div>
    </BaseDialog>
  );
}
